import { savedLocale } from './localePreference';
import { chatPuzzleText } from './chatTheme';
import { format, normalizeGameLocale, type Locale } from './locales';
import { localized } from './translations';
export type { Locale } from './locales';

// User-authored titles and puzzle letters are content and are never translated.
export const messages = {
  browseStageList: ['스테이지 목록', 'Stage list'],
  makerLocalIntro: [
    '넴을 배치하고 직접 풀어보세요. 초안은 자동으로 저장돼요.',
    'Place Nem and try your puzzle. Your draft saves automatically.',
  ],
  makerLocalWorkflow: ['구성 → 직접 테스트 → 초안 저장', 'Build → Test → Save draft'],
  attendanceTitle: ['출석 보상', 'Daily check-in'],
  tossPoints: ['토스 포인트', 'Toss Points'],
  tossRankedTitle: ['실시간 대전 보상', 'Live match reward'],
  tossPromotionPreparing: [
    '토스 포인트 보상은 준비 중이에요.',
    'Toss Points rewards are coming soon.',
  ],
  tossAttendanceOffer: [
    '출석하면 토스 포인트 {points}를 받아요. 하루 한 번 받을 수 있어요.',
    'Check in for {points}, once a day.',
  ],
  tossRankedOffer: [
    '랭크 매치나 1:1 초대 대전을 끝까지 플레이하면 토스 포인트 {points}를 받아요. 두 모드를 합쳐 하루 한 번 받을 수 있어요.',
    'Finish a ranked match or a 1:1 invite match for {points}. One reward per day across both modes.',
  ],
  tossBattleConditions: [
    '승패와 관계없이 끝까지 플레이한 경기만 인정돼요. 보상은 경기 당일에 받을 수 있으며, 중도 이탈·봇 연습·고스트는 제외돼요.',
    'Finish the whole match, win or lose. Claim on the day of the match. Leaving early, bot practice and ghosts do not count.',
  ],
  tossPromotionClaim: ['보상 받기', 'Claim reward'],
  tossPromotionCheck: ['보상 확인', 'Check reward'],
  tossBattleRewardGranted: ['토스 포인트 {points} 지급을 확인했어요.', '{points} confirmed.'],
  tossPromotionDailyLimit: [
    '오늘의 대전 보상은 다른 경기에서 이미 요청했어요.',
    'Today’s match reward was already requested for another match.',
  ],
  tossBattleRewardAutomatic: [
    '대전 보상은 자동으로 요청해요. 지급 상태는 대전 이력에서 볼 수 있어요.',
    'Match rewards are requested automatically. Check their status in Match history.',
  ],
  tossBattleRewardHistory: [
    '지급 결과를 확인 중이에요. 대전 이력에서 다시 확인할 수 있어요.',
    'The reward is not confirmed yet. Check it again in Match history.',
  ],
  tossPromotionReceived: ['확인 완료', 'Confirmed'],
  tossPromotionChecking: ['확인 중…', 'Checking…'],
  tossPromotionPending: [
    '지급 결과가 아직 확인되지 않았어요. 보상 확인을 누르면 결과만 다시 조회해요.',
    'The payout is not confirmed yet. Check reward refreshes the result without requesting another payout.',
  ],
  tossPromotionIneligible: [
    '이 경기의 보상 조건을 확인하지 못했어요. 경기 종료 직후라면 잠시 후 다시 확인해 주세요.',
    'This match is not eligible yet. If it just ended, check again shortly.',
  ],
  tossPromotionUnavailable: [
    '지금은 보상을 받을 수 없어요. 잠시 후 다시 확인해 주세요.',
    'Rewards are currently unavailable. Check again shortly.',
  ],
  tossPromotionAvailable: ['보상을 받을 수 있어요.', 'Your reward is ready to claim.'],
  tossPromotionFailed: [
    '보상을 확인하지 못했어요. 잠시 후 다시 시도해 주세요.',
    'Could not confirm the reward. Try again shortly.',
  ],
  tossPromotionConfirmed: ['토스 포인트 지급을 확인했어요.', 'Toss Points confirmed.'],
  attendanceRule: [
    '출석부에서 매일 출석하면 무료 힌트 3회, 최대 15회까지 모을 수 있어요. 한국 시간 기준이며 무료 힌트부터 사용해요.',
    'Check in on the calendar for 3 free hints each day, up to 15. Days reset at midnight Korea time. Free hints are used first.',
  ],
  attendanceBalance: ['이 중 출석 힌트 {n}회', 'Includes {n} attendance hints'],
  attendanceDevice: [
    '출석 힌트는 이 기기에만 저장돼요.',
    'Attendance hints are saved on this device only.',
  ],
  freeHelpTitle: ['무료 도움', 'Free help'],
  freeHelpUse: ['무료로 사용', 'Use for free'],
  freeHelpBalance: ['남은 힌트 {n}회', '{n} hints available'],
  freeHelpDailyRule: [
    '이 커뮤니티에서 처음 플레이하면 힌트 3회를 받아요. 이후 매일 첫 플레이에 3회씩, 최대 15회까지 모을 수 있어요. 날짜는 한국 시간 기준이에요.',
    'Get 3 hints when you first play in this community, then 3 on your first play each day, up to 15. Days reset at midnight Korea time.',
  ],
  freeContinueRule: [
    '시간이 끝나면 한 판에 한 번, 무료로 15초 더 플레이할 수 있어요.',
    'After time runs out, continue for 15 seconds free, once per run.',
  ],
  freeHelpExplanation: [
    'Reddit에서는 힌트와 이어하기가 무료예요. 도움을 쓴 결과는 별도 표시되고 별점·최고 기록·스트릭·대전 기록에는 반영되지 않아요.',
    'Hints and continues are free on Reddit. Assisted results are marked separately and do not earn stars, personal bests, streaks or competitive records.',
  ],
  freeHelpEmpty: [
    '힌트를 모두 사용했어요. 다음 날 첫 플레이에 다시 받을 수 있어요.',
    'You have used your hints. Play tomorrow to receive more.',
  ],
  freeHelpSignIn: [
    '무료 도움을 저장하고 사용하려면 Reddit에 로그인해 주세요. 퍼즐은 로그인 없이도 플레이할 수 있어요.',
    'Sign in to Reddit to save and use free help. You can still play puzzles without signing in.',
  ],

  chatMakerTitle: ['메시지 만들기', 'Build the conversation'],
  chatMoveUp: ['위로', 'Move up'],
  chatMoveDown: ['아래로', 'Move down'],
  chatMoveMessageUp: ['메시지 {n} 위로', 'Move message {n} up'],
  chatMoveMessageDown: ['메시지 {n} 아래로', 'Move message {n} down'],
  makerActionPlaying: [
    '직접 풀어보세요. 클리어하면 공유할 수 있어요.',
    'Solve your puzzle to share it.',
  ],
  makerActionDemo: [
    '연출 미리보기 중이에요. 공유하려면 직접 클리어해 주세요.',
    'Previewing the effect. Clear the puzzle yourself to share it.',
  ],
  chatMakerHint: [
    '메시지 길이와 보낸 사람을 정하고, 글자를 눌러 넴을 놓으세요.',
    'Choose the length and sender of each message, then tap a letter to place Nem.',
  ],
  chatMakerMessage: ['메시지 {n}', 'Message {n}'],
  chatMakerSide: ['보낸 사람', 'Sender'],
  chatMakerLeft: ['친구 · 왼쪽', 'Friend · Left'],
  chatMakerRight: ['나 · 오른쪽', 'You · Right'],
  chatPatternShort: ['짧은 대화', 'Short replies'],
  chatPatternMixed: ['길이 섞기', 'Mixed lengths'],
  chatPatternWall: ['장문 한 통', 'One long message'],

  battleOvertimeResult: ['연장전 포함 결과', 'Result after overtime'],
  makerActionTest: ['직접 클리어하면 공유할 수 있어요.', 'Clear your puzzle to unlock sharing.'],
  makerActionShare: ['클리어 완료! 이제 공유할 수 있어요.', 'Puzzle cleared. Ready to share.'],
  nextStage: ['다음 스테이지', 'Next stage'],
  makerStepBuild: ['구성하기', 'Build'],
  makerStepShare: ['공유하기', 'Share'],
  makerSections: ['제작 도구', 'Maker tools'],
  makerBoardPanel: ['보드 편집', 'Edit board'],
  makerRulesPanel: ['규칙 설정', 'Rules'],
  battleHalfHelp: [
    '정답이 상대보다 조금 늦게 도착해 0.5점을 받았어요.',
    'Your answer reached the server just after your opponent’s, earning 0.5 points.',
  ],
  battleFinalScore: ['최종 점수 {mine} : {other}', 'Final score {mine} : {other}'],
  stageProgress: ['내 진행 상황', 'Your progress'],
  stageCompleted: ['{total}개 중 {n}개 완료', '{n} of {total} completed'],
  makerPresets: ['어떤 퍼즐을 만들까요?', 'What will you make?'],
  makerPresetsChange: ['구성 바꾸기', 'Change preset'],
  puzzlePreview: ['정답을 숨긴 배치 미리보기', 'Board preview with the answer hidden'],
  puzzleTimeLimit: ['제한시간 {n}초', 'Time limit: {n}s'],
  puzzleCompleted: ['클리어', 'Cleared'],
  puzzleNotCompleted: ['아직 클리어 전', 'Not cleared yet'],
  puzzleRecordUnavailable: [
    '플레이 기록을 불러오지 못했어요. 퍼즐은 계속 고를 수 있어요.',
    'Could not load your play records. You can still choose a puzzle.',
  ],
  puzzleManage: ['관리', 'Manage'],
  makerPresetsClose: ['접기', 'Collapse presets'],
  makerPresetHint: [
    '모양을 고르고 배치와 규칙을 바꿔보세요.',
    'Choose a layout, then adjust the board and rules.',
  ],
  makerGridPreset: ['기본 보드', 'Classic board'],
  makerGrowingPreset: ['커지는 보드', 'Growing board'],
  makerCollapsePreset: ['와르르', 'Tumble'],
  makerChatPreset: ['대화창', 'Chat'],
  makerChatTitle: ['대화 속 넴', 'Nem in the chat'],
  makerChatStream: ['대화 이어가기', 'Growing chat'],
  chatWindowSize: ['찾아볼 메시지', 'Messages to search'],
  chatMessageLength: ['메시지당 글자 수', 'Letters per message'],
  chatRecent: ['최근 메시지 {n}개에서 넴을 찾아요', 'Find Nem in the latest {n} messages'],
  chatFixedLimit: [
    '고정 퍼즐로 바꾸려면 메시지 길이를 12글자 이하로 줄여 주세요.',
    'Shorten messages to 12 letters or fewer to switch to a fixed puzzle.',
  ],
  chatStreamHelp: [
    '맞힐 때마다 새 메시지가 와요. 넴은 최근 메시지 어디에나 숨을 수 있어요. 스크롤 중에는 시간이 멈춰요.',
    'Each find adds a message. Nem can hide in any of the latest messages. The timer pauses while the chat scrolls.',
  ],
  chatStreamInvalid: [
    '메시지는 3~4개, 각각 6~48글자로 만들고 넴은 하나만 넣어 주세요.',
    'Use 3–4 messages with 6–48 letters each and exactly one Nem.',
  ],
  chatDenseWarning: [
    '메시지가 길어 작은 화면에서는 글자가 작게 보여요. 테스트에서 확인해 주세요.',
    'Long messages make letters smaller on small screens. Check the playtest before sharing.',
  ],
  chatArrival: ['새 메시지가 왔어요', 'A new message arrived'],
  makerAdvanced: ['모양·물리 상세 설정', 'Layout & physics settings'],
  makerWorkflow: ['구성 → 직접 테스트 → 공유', 'Create → Playtest → Share'],
  makerNextTest: [
    '아래에서 직접 풀어보세요. 클리어하면 공유할 수 있어요.',
    'Test your puzzle below. Clear it to share.',
  ],
  makerNextShare: [
    '공유할 수 있어요. 수정하면 다시 클리어해야 해요.',
    'Ready to share. If you edit the puzzle, clear it again before sharing.',
  ],
  battleGuestStart: ['봇·고스트 시작', 'Try bots & ghosts'],
  battleGuestPlay: ['로그인 없이 먼저 플레이', 'Play before signing in'],
  battleGuestPlayHint: [
    '봇 연습·오늘의 고스트·초대 대전은 바로 즐길 수 있어요. 랭크 매치는 계정을 연결한 뒤 시작해요.',
    'Try bot practice, today’s ghost or an invite battle now. Connect your account to play ranked.',
  ],
  shopCancelTopup: ['충전 요청 취소', 'Cancel coin request'],
  shopCancelConfirmed: [
    '충전 요청을 취소했어요. 결제되지 않아요.',
    'Coin request cancelled. It will not charge you.',
  ],
  navigationMore: ['더 보기', 'More'],
  returnToGame: ['게임으로 돌아가기', 'Back to game'],
  browsePauseNote: [
    '돌아가면 현재 퍼즐을 이어해요. 다른 퍼즐을 고르면 새로 시작해요.',
    'Return to continue your puzzle. Choosing another starts a new run.',
  ],
  firstUncleared: ['{n}번부터 이어 풀기', 'Continue at stage {n}'],
  currentStage: ['최근 플레이: {n}번', 'Last played: stage {n}'],
  shareLocked: ['클리어 후 공유', 'Clear to share'],
  editorFit: ['전체 보기', 'Overview'],
  editorEditSize: ['편집 크기', 'Edit size'],
  editorOverviewHint: [
    '전체 모양을 확인하는 보기예요. 수정하려면 편집 크기를 누르세요.',
    'Preview the whole board. Choose Edit size to paint.',
  ],
  editorPanHint: [
    '숨은 칸은 드래그해서 확인하세요. 전체 보기로 모양을 확인할 수 있어요.',
    'Drag to reach hidden cells, or use Overview to see the whole board.',
  ],
  shopPackHelpValue: [
    '힌트 최대 {hint}회 또는 이어하기 {continue}회',
    'Up to {hint} hints or {continue} continues',
  ],
  shopCreditsPending: ['1990 크레딧 · 판매 준비 중', '1990 Credits · Not on sale yet'],
  shopGoldPending: ['Reddit Gold · 판매 준비 중', 'Reddit Gold · Not on sale yet'],
  shopPricePending: ['판매 가격을 확인할 수 없어요', 'Price not available yet'],
  shopRedditCaption: [
    'Reddit Gold로 넴 코인을 충전한 뒤 도움 상품을 교환해요.',
    'Use Reddit Gold to get Nem Coins, then exchange them for help items.',
  ],
  shopHelpRules: [
    '힌트는 플레이 중, 이어하기는 시간 초과 후 사용할 수 있어요. 도움을 쓴 기록은 별점·최고 기록·대전 순위에 반영하지 않아요.',
    'Use a hint during play or continue after time runs out. Assisted runs do not earn stars, personal bests or ranked results.',
  ],
  botStartHint: [
    '넴을 누르면 60초 봇 연습이 시작돼요. RP에는 영향이 없어요.',
    'Press Nem for 60 seconds of bot practice. RP is unchanged.',
  ],
  ghostStartHint: [
    '넴을 누르면 60초 기록 도전이 시작돼요. 비공식 개인 기록이에요.',
    'Press Nem to start a 60-second ghost challenge. This is an unofficial personal record.',
  ],
  shopAlreadyCompleted: [
    '이미 결제된 요청이라 넴 코인을 받았어요.',
    'This request was already paid. Your Nem Coins were delivered.',
  ],
  shopTitle: ['상점', 'Shop'],
  shopWallet: ['내 코인과 이용권', 'My coins and items'],
  shopCoins: ['넴 코인', 'Nem Coins'],
  shopInventory: ['힌트 {hint}회 · 이어하기 {continue}회', 'Hints: {hint} · Continues: {continue}'],
  shopHelpTitle: ['도움 상품', 'Help items'],
  shopHelpCaption: [
    '넴 코인으로 힌트와 이어하기를 교환하세요.',
    'Exchange Nem Coins for hints and continues.',
  ],
  shopHintItem: ['힌트 {n}회', '{n} hint'],
  shopContinueItem: ['이어하기 {n}회', '{n} continue'],
  shopBundleItem: ['도움 묶음', 'Help bundle'],
  shopBundleSaving: [
    '따로 교환할 때보다 {n}코인 절약',
    'Save {n} coins compared with individual items',
  ],
  shopHintDescription: [
    '넴이 있는 구역을 잠깐 보여줘요.',
    'Briefly highlights the area containing Nem.',
  ],
  shopContinueDescription: [
    '시간이 끝난 판을 15초 더 이어가요. 한 판에 한 번 사용할 수 있어요.',
    'Continue a timed-out puzzle for 15 more seconds, once per run.',
  ],
  shopBundleDescription: [
    '힌트 {n}회와 이어하기 {n}회가 들어 있어요.',
    'Includes {n} hints and {n} continues.',
  ],
  shopExchange: ['{n} 코인으로 교환', 'Exchange for {n} coins'],
  shopTopup: ['코인 충전', 'Get coins'],
  shopPriceChanged: [
    '상품 가격이 바뀌었어요. 상점을 다시 열어 확인해 주세요.',
    'The item price changed. Reopen the shop to check it.',
  ],
  shopCoinsAmount: ['{n} 코인', '{n} coins'],
  shopCreditPrice: ['{n} 크레딧', '{n} Credits'],
  shopTestPrice: ['테스트 충전', 'Test top-up'],
  shopRefresh: ['다시 확인', 'Refresh'],
  shopLoading: ['잔액과 상품을 확인하고 있어요.', 'Checking your balance and products.'],
  shopCheckingResult: [
    '요청을 처리하고 있어요. 잠시 기다려 주세요.',
    'Processing your request. Please wait.',
  ],
  shopLoadingOffers: ['가격 확인 중', 'Checking price'],
  shopOffersUnavailable: ['가격을 확인하지 못했어요.', 'Price is unavailable.'],
  shopPricesAfterSignIn: ['로그인 후 가격을 확인할 수 있어요.', 'Sign in to see prices.'],
  shopPricesRetry: [
    '충전 가격을 불러오지 못했어요. 보유한 코인으로 상품을 교환할 수 있어요.',
    'Coin prices could not load. You can still exchange the coins you have.',
  ],
  shopRecoveryTitle: ['이전 구매·교환 요청', 'Previous purchases and exchanges'],
  shopStorageUnavailable: [
    '이 브라우저에서는 구매 요청을 안전하게 저장할 수 없어요.',
    'This browser cannot safely save purchase requests.',
  ],
  shopReopen: ['상점 다시 열기', 'Reopen shop'],
  shopLoginRetry: [
    '로그인을 마치지 못했어요. 다시 시도해 주세요.',
    'Sign-in could not finish. Please try again.',
  ],
  shopSignIn: [
    '로그인하면 넴 코인과 이용권을 확인할 수 있어요.',
    'Sign in to view your Nem Coins and help items.',
  ],
  shopSignInButton: ['로그인', 'Sign in'],
  shopReconnectStore: ['Store 다시 연결', 'Reconnect Store'],
  nativeSessionUnavailable: [
    '저장된 계정 정보를 읽지 못했어요. 앱을 다시 열어 주세요.',
    'Could not read your saved account details. Reopen the app.',
  ],
  shopAccountChanged: [
    '계정이 바뀌었어요. 상점을 다시 열어 주세요.',
    'Your account changed. Reopen the shop to continue.',
  ],
  shopSalesClosed: [
    '새 코인 충전은 준비 중이에요. 이전 요청은 계속 확인할 수 있어요.',
    'New coin purchases are not available yet. You can still check previous requests.',
  ],
  shopTopupCaption: [
    '결제로 넴 코인을 충전한 뒤 도움 상품을 교환해요.',
    'Purchase Nem Coins, then exchange them for help items.',
  ],
  shopFoundationCaption: [
    '1990 크레딧으로 넴 코인을 충전한 뒤 도움 상품을 교환해요.',
    'Use 1990 Credits to get Nem Coins, then exchange them for help items.',
  ],
  shopCoinPack: ['넴 코인 {n}개', '{n} Nem Coins'],
  shopUnavailable: [
    '아직 구매와 교환을 이용할 수 없어요.',
    'Purchases and exchanges are not available yet.',
  ],
  shopInsufficient: ['넴 코인이 부족해요.', 'You need more Nem Coins.'],
  shopPurchaseNotStarted: [
    '결제 창을 열지 못했어요. 결제는 진행되지 않았으니 다시 시도해 주세요.',
    'Checkout could not open. No purchase was started; please try again.',
  ],
  shopPurchaseCancelled: ['결제를 취소했어요.', 'Purchase cancelled.'],
  shopPurchaseRevoked: [
    '취소된 주문이에요. 잔액을 다시 확인해 주세요.',
    'This order is no longer valid. Refresh your balance.',
  ],
  shopBalanceSyncPending: [
    '구매 내역을 반영하고 있어요. 잠시 후 잔액을 다시 확인해 주세요.',
    'Your purchase history is updating. Check your balance again shortly.',
  ],
  shopBalanceSyncUnavailable: [
    '잔액을 확인하지 못했어요. 잠시 후 다시 확인해 주세요.',
    'Your balance could not be checked. Please try again shortly.',
  ],
  shopPurchaseRefunded: [
    '환불된 주문이에요. 현재 잔액을 다시 확인해 주세요.',
    'This order was refunded. Refresh your balance.',
  ],
  shopPaymentRejected: [
    '구매를 확인하지 못했어요. 새로 결제하지 말고 기존 요청을 확인해 주세요.',
    'This purchase could not be verified. Check the existing request before buying again.',
  ],
  shopSecureStorageUnavailable: [
    '결제 기록을 읽지 못했어요. 저장된 데이터를 지우지 말고 다시 시도해 주세요.',
    'Could not read your purchase history. Keep your saved data and try again.',
  ],
  shopPaymentPending: [
    '결제 결과를 확인하고 있어요. 다시 확인해도 결제 창은 열리지 않아요.',
    'Payment is not confirmed yet. Checking again will not reopen checkout.',
  ],
  shopWalletPreparing: [
    '지금은 잔액 조회·충전·교환을 이용할 수 없어요.',
    'Balance checks, coin purchases and exchanges are currently unavailable.',
  ],
  shopLoadError: [
    '상점 정보를 불러오지 못했어요. 잠시 후 다시 확인해 주세요.',
    'Could not load shop information. Please try again shortly.',
  ],
  shopRetry: [
    '처리 결과를 확인하지 못했어요. 다시 확인하면 중복 결제되지 않아요.',
    'The result is not confirmed. Check again without being charged twice.',
  ],
  shopPendingNote: [
    '이전 요청을 확인한 뒤 새 상품을 선택할 수 있어요.',
    'Check the previous request before choosing another item.',
  ],
  shopHelpHistoryUnavailable: [
    '이전 도움 요청을 불러오지 못했어요. 잠시 후 다시 확인해 주세요.',
    'Previous help requests could not be loaded. Please try again shortly.',
  ],
  shopResetCancel: ['취소', 'Cancel'],
  shopResetRequest: ['구매 요청 초기화', 'Reset purchase request'],
  shopResetConfirm: ['주문 확인 후 초기화', 'Check orders and reset'],
  shopResetDescription: [
    '토스 결제 내역을 먼저 확인해 주세요. 이 기능은 상점의 대기 요청만 정리하며 결제를 취소하거나 환불하지 않아요. 지급을 확인해야 할 주문이 있으면 초기화하지 않고, 늦게 확인된 결제는 계속 복구해요.',
    'Check your Toss payment history first. This clears the pending shop request; it does not cancel or refund a payment. Orders still awaiting delivery block the reset, and late payments remain recoverable.',
  ],
  shopResetDone: [
    '구매 요청을 초기화했어요. 상품을 다시 선택할 수 있어요.',
    'Purchase request reset. You can choose a product again.',
  ],
  shopResetPending: [
    '지급을 확인해야 할 주문이 남아 있어요. 처리 결과를 먼저 확인해 주세요.',
    'An order still needs delivery confirmation. Check its result before resetting.',
  ],
  shopCheckPending: ['처리 결과 확인', 'Check result'],
  shopSandbox: [
    '상점 체험 · 표시된 재화와 가격은 테스트용이며 실제 결제되지 않아요.',
    'Shop preview · Test coins and prices. No real payment.',
  ],
  assistedComplete: ['도움받아 완료', 'Completed with help'],
  assistedLabel: ['도움 사용', 'Assisted'],
  stageLocked: ['잠김', 'Locked'],
  stageUnlockHint: ['{n}번을 클리어하면 열려요', 'Clear stage {n} to unlock'],
  stageChat: ['대화창', 'Chat'],
  stageProgressHint: [
    '앞 스테이지를 클리어하면 다음이 열려요. 이전 페이지에서 별점에 다시 도전할 수 있어요.',
    'Clear a stage to unlock the next. Revisit earlier pages to improve your stars.',
  ],
  timeoutReveal: ['시간이 다 됐어요', 'Time is up'],
  timeoutAnswer: ['표시된 넴이 정답이었어요.', 'The highlighted Nem was the answer.'],
  makerSavedReady: [
    '초안이 저장돼요. 다시 테스트하거나 수정할 수 있어요.',
    'Your draft is saved. Test again or keep editing.',
  ],
  helpRecoveryTitle: ['받지 못한 도움 복구', 'Recover undelivered help'],
  helpRecoveryNote: [
    '연결이 끊겨 적용되지 않은 힌트·이어하기를 확인해요. 이미 사용한 이용권은 다시 차감하지 않아요.',
    'Recover a hint or extra time interrupted by a lost connection. Items already used are not charged again.',
  ],
  helpRecoveryEmpty: ['확인할 도움 요청이 없어요.', 'No help requests to check.'],
  helpRecoveryResume: ['퍼즐 이어하기', 'Resume puzzle'],
  helpAdPrivacy: ['광고 개인정보 설정', 'Ad privacy options'],
  helpRewardLimited: [
    '지금은 광고 보상을 받을 수 없어요. 나중에 다시 시도해 주세요.',
    'Ad rewards are currently unavailable. Try again later.',
  ],
  helpAdUnavailable: [
    '지금은 광고를 불러올 수 없어요. 잠시 후 다시 시도해 주세요.',
    'No ad is available right now. Please try again later.',
  ],
  helpRewardCheck: ['보상 확인', 'Check reward'],
  helpRewardRequested: ['광고 보상 확인 대기', 'Awaiting ad reward confirmation'],
  helpRewardPending: [
    '광고 보상을 확인 중이에요. 다시 확인해도 광고는 재생되지 않아요.',
    'Your ad reward is pending. Checking again will not replay the ad.',
  ],
  helpRewardStocked: [
    '받은 이용권을 보관했어요. 원할 때 사용해 주세요.',
    'Your earned help item is saved. Use it when you like.',
  ],
  helpRequestCancelled: ['도움 요청을 취소했어요.', 'Help request cancelled.'],
  helpAlreadyDelivered: [
    '이미 전달된 도움이에요. 추가로 사용하지 않았어요.',
    'This help was already delivered. No extra item was used.',
  ],
  helpRecoveryCancel: ['요청 취소', 'Cancel request'],
  helpRecoveryConfirmed: ['사용 완료 · 추가 차감 없음', 'Consumed · No extra item used'],
  helpRecoveryRequested: [
    '사용 여부 확인 필요 · 이어할 때 확인해요',
    'Item use not confirmed · checked when you resume',
  ],
  helpResumeQuestion: ['이어서 시작할까요?', 'Ready to resume?'],
  helpResume: ['이어서 시작', 'Resume play'],
  helpNoItem: [
    '이용권이 부족해요. 상점에서 교환해 주세요.',
    'Not enough help items. Exchange coins in the shop.',
  ],
  helpDeliveryPending: [
    '도움 사용 여부를 다시 확인할게요.',
    'Checking whether the help item was used.',
  ],
  helpHint: ['힌트', 'Hint'],
  helpContinue: ['15초 이어하기', 'Continue for 15s'],
  helpAd: ['광고 보고 사용', 'Watch ad to use'],
  helpInventory: ['이용권으로 사용', 'Use help item'],
  helpExplanation: [
    '도움을 사용한 결과는 별점·최고 기록과 따로 저장돼요.',
    'Assisted results are saved separately from stars and best times.',
  ],
  helpUnavailable: ['지금은 도움을 사용할 수 없어요.', 'Help is not available right now.'],
  helpFailed: [
    '처리 결과를 확인하지 못했어요. 다시 확인해 주세요.',
    'The result is not confirmed. Please check again.',
  ],
  helpLimited: [
    '요청이 많아요. 잠시 기다린 뒤 다시 시도해 주세요. 도움은 사용되지 않았어요.',
    'Too many requests. Please wait a moment and try again. No help was used.',
  ],
  hapticsOn: ['진동 켜기', 'Turn vibration on'],
  hapticsOff: ['진동 끄기', 'Turn vibration off'],
  resultPost: ['도전 게시하기', 'Post a challenge'],
  resultPostConfirm: ['내 계정으로 게시', 'Post as me'],
  resultPostAccount: [
    'u/{user} 계정으로 r/{community}에 아래 내용과 플레이 가능한 퍼즐을 게시합니다.',
    'Post the text below and a playable puzzle to r/{community} as u/{user}.',
  ],
  resultPostLoading: ['게시 내용을 준비하고 있어요…', 'Preparing your post…'],
  resultPostBusy: ['게시 중이에요…', 'Posting…'],
  resultPostDone: [
    '공유 글을 열 수 있어요. 오늘 같은 퍼즐을 공유했다면 기존 글로 연결됩니다.',
    'Your post is ready. If you shared this puzzle today, this opens your existing post.',
  ],
  resultPostOpen: ['게시물 보기', 'View post'],
  resultPostError: [
    '게시를 완료하지 못했어요. 잠시 후 다시 확인해 주세요.',
    'Could not complete your post. Please check again shortly.',
  ],
  resultPostUncertain: [
    '게시 결과를 확인하지 못했어요. 중복 게시를 피하려면 Reddit 프로필에서 먼저 확인해 주세요.',
    'The result is uncertain. Check your Reddit profile first to avoid a duplicate post.',
  ],
  resultPostLimited: [
    '도전 게시물은 10분에 하나씩 올릴 수 있어요. 같은 퍼즐은 하루에 한 번 공유합니다.',
    'You can post one challenge every 10 minutes and share each puzzle once a day.',
  ],
  resultPostLogin: ['Reddit에 로그인한 뒤 다시 시도해 주세요.', 'Sign in to Reddit and try again.'],
  resultPostSource: [
    '원래 퍼즐을 확인할 수 없어요. 게시물을 다시 열어 주세요.',
    'The original puzzle is unavailable. Please reopen its post.',
  ],
  copyResult: ['결과 복사', 'Copy result'],
  resultCopied: ['복사했어요', 'Copied'],
  copyManually: ['텍스트를 선택해서 복사해 주세요.', 'Select this text to copy it.'],
  guideTitle: ['넴을 찾아볼까요?', 'Meet Nem'],
  guideFind: [
    '윗획 두 개가 없는 넴을 찾아보세요. 시간 제한은 없어요.',
    'Find the one without the two little horns. There is no timer.',
  ],
  guideTransfer: [
    '다른 넵의 윗획 두 개가 방금 찾은 넴으로 옮겨 와요. 이제 획이 없는 넴을 다시 찾아보세요.',
    'The two top strokes move from another Nep to the Nem you found. Find the new Nem without them.',
  ],
  guideDone: [
    '잘 찾았어요! 퍼즐에 따라 글자들이 섞이거나 움직이기도 해요.',
    'You found it! In some puzzles, the letters shuffle or move after each find.',
  ],
  guideTile: ['연습 칸', 'Practice tile'],
  guidePlay: ['준비됐어요', 'Got it'],
  guideSkip: ['건너뛰기', 'Skip'],
  guideClose: ['닫기', 'Close'],
  guideHelp: ['플레이 방법', 'How to play'],
  choosePreset: ['프리셋 선택', 'Choose a preset'],
  hitConfirmed: ['찾았넴!', 'Found Nem!'],
  nextStageNumber: ['다음 #{n}', 'Next #{n}'],
  hitMoving: ['움직이는 중…', 'Moving…'],
  battleRanked: ['랭크 매치', 'Ranked match'],
  battleGuestRankedHint: [
    '가입 없이 2,500 RP로 시작해요. 나중에 계정을 연결할 수 있어요.',
    'Start at 2,500 RP without signing up. Link an account later.',
  ],
  battleGuestRankedSaved: [
    '계정을 연결하면 다른 기기에서도 기록을 이어갈 수 있어요. 연결 전 브라우저 데이터를 지우면 기록을 잃을 수 있어요. 기존 계정에 RP가 있으면 그 기록을 유지해요.',
    'Link an account to keep playing on other devices. Clearing browser data before linking may lose your record. An account with existing RP keeps that record.',
  ],
  battleLinkAccount: ['계정 연결', 'Link account'],
  battleGuestHint: ['로그인하면 2,500 RP부터 시작해요.', 'Sign in to start at 2,500 RP.'],
  battlePlacement: ['배치 중 · {n}/10경기', 'Placement · {n}/10 matches'],
  battleRated: ['승패·무승부로 RP가 바뀌어요', 'RP changes with wins, losses and draws'],
  battleSearchingMatch: ['상대를 찾고 있어요 · {n}초', 'Finding an opponent · {n}s'],
  battleCancel: ['매칭 취소', 'Cancel search'],
  battleHalf: ['거의 동시에 찾았어요! +0.5', 'Almost together! +0.5'],
  battleWrong: ['여기는 넵이에요', 'That one is Nep'],
  battleBoardChanged: ['보드가 바뀌었어요', 'The board has changed'],
  battleTapUnconfirmed: [
    '입력을 확인하지 못했어요. 다시 눌러 주세요.',
    'Could not confirm that tap. Try again.',
  ],
  remixPuzzle: ['이 퍼즐로 만들기', 'Remix puzzle'],
  remixOf: ['원작: {source}', 'Remix of {source}'],
  remixDailySource: ['데일리 {day}', 'Daily {day}'],
  remixStageSource: ['스테이지 {n}', 'Stage {n}'],
  remixCommunitySource: ['커뮤니티 퍼즐', 'Community puzzle'],
  remixOriginal: ['원작 보기', 'View original'],
  allowRemix: ['리믹스 허용', 'Allow remixes'],
  remixAllowed: ['출처를 표시하면 허용', 'Allow with credit'],
  remixBlocked: ['허용하지 않음', 'Do not allow'],
  remixUnavailable: [
    '원작을 확인할 수 없거나 리믹스가 허용되지 않았어요.',
    'The original is unavailable or does not allow remixes.',
  ],
  dailyGhost: ['오늘의 고스트 도전', "Today's ghost challenge"],
  dailyGhostHint: [
    '60초 · 모두 같은 보드 순서 · RP에 영향 없음',
    '60 seconds · Same board sequence for everyone · No RP changes',
  ],
  dailyGhostResult: [
    '{day} 고스트 · 60초에 {n}개 · 비공식',
    '{day} ghost · {n} found in 60s · Unofficial',
  ],
  puzzleStats: ['플레이 통계', 'Puzzle stats'],
  closeStats: ['닫기', 'Close'],
  statsPeriod: ['최근 {n}일 · 이 퍼즐', 'Last {n} days · This puzzle'],
  statsRefresh: ['새로고침', 'Refresh'],
  statsClearRate: ['클리어율', 'Clear rate'],
  statsAttemptLabel: ['시도', 'Attempts'],
  statsClearLabel: ['클리어', 'Clears'],
  statsTimeoutLabel: ['시간 초과', 'Timed out'],
  statsUnfinishedLabel: ['결과 미수신', 'No result yet'],
  statsClearTime: ['완료 시간', 'Clear time'],
  statsNoClears: ['아직 클리어 기록이 없어요.', 'No clears recorded yet.'],
  statsStarDistribution: ['별점 분포', 'Star ratings'],
  statsStarCount: ['별 {stars}개 · {n}회', '{stars} stars · {n} clears'],
  statsEmpty: ['아직 수집된 플레이 기록이 없어요.', 'No play data collected yet.'],
  statsAttempts: ['플레이 시도 {n}회', '{n} attempts'],
  statsClears: ['클리어 {n}회 · {rate}%', '{n} clears · {rate}%'],
  statsMedian: ['클리어 시간 중앙값: 약 {min}–{max}초', 'Median clear time: about {min}–{max}s'],
  statsMedianAbove: ['클리어 시간 중앙값: {min}초 이상', 'Median clear time: {min}s or more'],
  statsStars: ['★ {a}회 · ★★ {b}회 · ★★★ {c}회', '★ {a} · ★★ {b} · ★★★ {c}'],
  statsSmallSample: ['시도 30회부터 난이도를 표시해요.', 'Difficulty appears after 30 attempts.'],
  statsEasy: ['클리어율 기준: 쉬움', 'Clear-rate difficulty: Easy'],
  statsMedium: ['클리어율 기준: 보통', 'Clear-rate difficulty: Medium'],
  statsHard: ['클리어율 기준: 어려움', 'Clear-rate difficulty: Hard'],
  statsPersonal: [
    '익명 시도 집계 · 이용자 수나 공식 기록이 아니에요.',
    'Anonymous attempts, not unique players or verified scores.',
  ],
  statsUnavailable: [
    '지금은 통계를 불러올 수 없어요. 잠시 후 다시 열어 주세요.',
    'Stats are unavailable. Try opening them again later.',
  ],
  dailyStreak: ['데일리 {n}일 연속', '{n}-day daily streak'],
  battleWorking: ['처리 중…', 'Working…'],
  battleOvertime: ['동점! 15초 연장 · 한 번만 진행해요', 'Tied! One 15-second overtime'],
  battleFallback: [
    '상대를 찾지 못했어요. 봇이나 고스트와 플레이할 수 있어요.',
    'No opponent found. You can play against a bot or a ghost.',
  ],
  battleSolo: ['혼자 플레이', 'Play solo'],
  battleBot: ['봇 연습', 'AI practice'],
  battleGhost: ['고스트 대결', 'Race a ghost'],
  ghostFind: ['기록보다 빨리 찾아요', 'Beat the recorded run'],
  ghostCreate: ['내 고스트 만들기', 'Record a ghost'],
  battleNoRewards: ['RP·보상 없음', 'No RP or rewards'],
  battleBotLabel: ['AI 연습 상대', 'AI opponent'],
  battleGhostLabel: ['고스트 기록', 'Ghost'],
  ghostCode: ['고스트 공유 코드', 'Ghost share code'],
  ghostRace: ['시작', 'Race'],
  ghostCopy: ['고스트 코드 복사', 'Copy ghost code'],
  ghostInvalid: ['올바른 고스트 공유 코드를 입력해 주세요.', 'Enter a valid ghost share code.'],
  ghostSaveFailed: [
    '기록을 저장하지 못했어요. 고스트 코드를 복사해 보관해 주세요.',
    'Could not save. Copy your ghost code to keep it.',
  ],
  battleNoMatch: [
    '아직 상대가 없어요. 잠시 후 다시 찾아 주세요.',
    'No opponent yet. Try again shortly.',
  ],
  battleLogin: ['로그인', 'Sign in'],
  battleLoginHint: [
    '계정으로 로그인한 뒤 다시 확인해 주세요.',
    'Sign in with your account, then check again.',
  ],
  battleLoginPreparing: [
    '랭크 매치 계정 연결을 준비 중이에요. 초대 대전과 혼자 도전은 이용할 수 있어요.',
    'Ranked sign-in is not connected yet. Invite battles and solo challenges are available.',
  ],
  battleRatingRefresh: ['RP 확인', 'Refresh RP'],

  effectDemo: ['연출 미리보기', 'Preview effect'],
  effectDemoHint: [
    '연출 미리보기 중이에요. 공유하려면 직접 클리어해 주세요.',
    'Previewing the effect. Clear the puzzle yourself to share it.',
  ],
  animationClockHint: [
    '혼자 플레이할 때는 정답 연출 중 제한시간과 기록 시간이 멈춰요.',
    'In solo play, the timer and recorded time pause during answer transitions.',
  ],
  motionPreset: ['연출 프리셋', 'Effect preset'],
  draftNeedsTitle: ['제목을 입력하면 테스트할 수 있어요.', 'Enter a title before testing.'],
  draftNeedsLetters: [
    '넴과 넵을 각각 하나 이상 배치해 주세요.',
    'Place at least one Nem and one Nep.',
  ],
  draftNeedsRectangle: [
    '회전에는 빈 행이 없는 2~8행 직사각형이 필요해요.',
    'Rotation requires a complete rectangle with 2–8 rows.',
  ],
  draftNeedsSupports: [
    '빠질 받침 개수보다 목표가 커요. 목표를 줄여 주세요.',
    'The goal exceeds the available supports. Reduce the goal.',
  ],

  battle: ['실시간 대전', 'Live multiplayer'],
  startQuestion: ['시작하시겠습니까?', 'Ready to start?'],
  copyInviteLink: ['초대 링크 복사', 'Copy invite link'],
  shareLink: ['공유하기', 'Share'],
  shareOpening: ['공유창 여는 중…', 'Opening share…'],
  resultSaving: ['기록 저장 중…', 'Saving your result…'],
  resultSaved: ['기록을 저장했어요.', 'Your result is saved.'],
  resultSaveFailed: [
    '기록을 저장하지 못했어요. 다시 시도해 주세요.',
    'Could not save your result. Please try again.',
  ],
  resultSaveRetry: ['저장 다시 시도', 'Retry saving'],
  inviteShareUnavailable: [
    '공유 창을 열지 못했어요. 다시 시도해 주세요.',
    'Could not open sharing. Please try again.',
  ],
  friendInviteText: [
    '넵넵넴넵에서 같이 대결해요! 초대 코드: {code}',
    'Join me for a match in Nep Nep Nem Nep! Invite code: {code}',
  ],
  puzzleShareText: [
    '넵 사이에 숨은 넴, 같이 찾아볼래요?',
    'Can you find the Nem hiding among the Neps?',
  ],
  invitedTitle: ['친구가 대결에 초대했어요', 'You’re invited to a match'],
  invitedJoin: ['초대방 입장', 'Join invited room'],
  startWithNem: ['넴을 눌러 시작하세요', 'Press Nem to start'],
  readyWithNem: ['넴을 눌러 준비를 마치세요', 'Press Nem when you’re ready'],
  soloStart: ['시작', 'Start'],
  soloLeave: ['도전 나가기', 'Exit challenge'],
  soloFormat: ['60초 · 기록 도전', '60s · Record challenge'],
  soloChoices: ['도전 선택', 'Choose a challenge'],
  soloChallenges: ['혼자 도전', 'Solo challenges'],
  soloIntro: [
    '봇과 연습하거나 고스트 기록에 도전해 보세요.',
    'Practice against a bot or try to beat a ghost.',
  ],
  playModes: ['플레이 방식', 'Play mode'],
  battlePaused: [
    '지금은 새 상대를 찾을 수 없어요. 초대 대전과 혼자 플레이는 이용할 수 있어요.',
    'Matchmaking is paused. Invite battles and solo play are still available.',
  ],
  battleUpdateRequired: [
    '게임이 업데이트됐어요. 새로고침한 뒤 다시 연결해 주세요.',
    'The game has been updated. Refresh to reconnect.',
  ],
  battleIntro: ['같은 보드에서 넴을 찾아 겨뤄요.', 'Race to find Nem on the same board.'],
  battleFormat: ['1:1 · 60초', '1:1 · 60s'],
  battleOnline: ['온라인 대전', 'Online'],
  battleFriends: ['초대 대전', 'Invite battle'],
  battleInviteHint: [
    '초대 코드로 친구와 대결하세요. RP는 바뀌지 않아요.',
    'Invite a friend with a room code. This match does not affect RP.',
  ],
  battleRules: ['대전 규칙', 'How to play'],
  battleRuleScore: [
    '먼저 찾으면 1점, 0.3초 안에 따라 찾으면 0.5점. 첫 정답에 5×5로, 이후 행·열을 번갈아 늘려 최대 8×11까지 커져요.',
    'First find: 1 point. A follow-up within 0.3s: 0.5. Start at 4×4, reach 5×5 on the first find, then add a row or column per find up to 8×11.',
  ],
  battleRuleTime: [
    '60초 후 동점이면 15초 동안 한 번 연장해요. 오답을 누르면 0.4초 뒤에 다시 입력할 수 있어요.',
    'A tie after 60 seconds gets one 15-second overtime. After a wrong tap, wait 0.4 seconds before tapping again.',
  ],
  battleCodePlaceholder: ['초대 코드 입력', 'Enter invite code'],
  ghostCodePlaceholder: ['고스트 코드 붙여넣기', 'Paste ghost code'],
  ghostImport: ['고스트 코드로 대결', 'Race a shared ghost'],
  ghostHint: [
    '같은 순서의 보드를 풀며 저장된 기록과 겨뤄요.',
    'Solve the same board sequence against a saved run.',
  ],
  battleInvite: ['방 만들기', 'Create room'],
  battleCode: ['초대 코드', 'Invite code'],
  battleJoin: ['참가', 'Join'],
  battleReady: ['준비 완료', 'Ready'],
  battleBoardHidden: ['시작하면 보드가 열려요', 'The board appears when play begins'],
  battleWaiting: ['상대와 함께 준비해 주세요', 'Waiting for both players to be ready'],
  battleYou: ['나', 'You'],
  battleOpponent: ['상대', 'Opponent'],
  battleSearching: ['상대를 기다리는 중', 'Waiting for opponent'],
  battleLeave: ['방 나가기', 'Leave room'],
  battleCopy: ['초대 코드 복사', 'Copy invite code'],
  battleCopied: ['복사했어요', 'Copied'],
  battleConnecting: [
    '연결 중 · 경기는 계속 진행돼요',
    'Reconnecting · the match clock keeps running',
  ],
  battleFind: ['넴을 먼저 찾아요', 'Find Nem first'],
  battleWon: ['승리!', 'You won!'],
  battleLost: ['아쉽게 졌어요', 'You lost'],
  battleDraw: ['무승부!', 'A draw!'],
  battleHitYou: ['찾았어요! +1', 'You found Nem! +1'],
  battleHitOther: ['상대가 찾았어요', 'Opponent found Nem'],
  battleError: [
    '연결하지 못했어요. 잠시 후 다시 시도해 주세요.',
    'Could not connect. Please try again shortly.',
  ],
  battleUnavailable: [
    '지금은 실시간 대전을 이용할 수 없어요.',
    'Live multiplayer is currently unavailable.',
  ],
  battleLoginExpired: [
    '로그인 연결이 만료됐어요. 다시 로그인해 주세요. 초대 대전과 혼자 도전은 이용할 수 있어요.',
    'Your sign-in link expired. Sign in again, or try an invite battle or solo challenge.',
  ],
  battleLoginUnavailable: [
    '로그인 연결을 확인하지 못했어요. 초대 대전과 혼자 도전은 이용할 수 있어요.',
    'Sign-in could not be verified. Invite battles and solo challenges are available.',
  ],
  battleSignIn: [
    '랭크 매치는 계정 연결이 필요해요. 초대 대전과 혼자 도전은 바로 할 수 있어요.',
    'Connect your account for ranked matches. Invite battles and solo challenges are available without sign-in.',
  ],
  battleRate: ['잠시 기다린 뒤 다시 시도해 주세요.', 'Please wait a moment before trying again.'],
  battleFull: ['이미 시작했거나 가득 찬 방이에요.', 'This room is full or has already started.'],
  battleInviteExpired: [
    '초대 방이 만료되어 대전 로비로 이동했어요. 새 방을 만들거나 다른 초대를 받아 주세요.',
    'This invitation has expired. You’re back in the lobby. Create a room or use a new invitation.',
  ],
  battleInviteUnavailable: ['초대 방에 들어갈 수 없어요', 'Unable to join this room'],
  battleInviteNew: ['새 방 만들기', 'Create a new room'],
  battleRematchUnavailable: [
    '재대결을 시작할 수 없어요. 새 방에서 다시 시작해 주세요.',
    'A rematch is no longer available. Start again in a new room.',
  ],
  battleInviteMissing: [
    '이 초대 코드에 해당하는 방을 찾지 못했어요. 코드를 확인하거나 새 초대를 받아 주세요.',
    'No room was found for this invitation code. Check the code or ask for a new invitation.',
  ],
  battleExpired: [
    '방이 만료됐어요. 새 방을 만들어 주세요.',
    'This room has expired. Please create another.',
  ],
  battleScope: ['초대전은 RP에 영향 없어요', 'Invite games are unrated'],
  battleRedditScope: [
    '이 Reddit 커뮤니티의 매칭 · 초대전은 RP에 영향 없어요',
    'Matchmaking in this Reddit community · invite games are unrated',
  ],
  battleCountdown: ['{n}초 후 시작', 'Starting in {n}s'],

  title: ['넵넵넴넵', 'Nep Nep Nem Nep'],
  bootError: [
    '게임을 불러오지 못했어요. 연결을 확인하고 다시 불러와 주세요.',
    'Could not load the game. Check your connection and reload.',
  ],
  subtitle: ['넵 사이에 숨은 넴을 찾아라!', 'Find 넴 hiding among 넵!'],
  daily: ['오늘의 넴 · {day}', 'Daily Nem · {day}'],
  board: [
    '넴 찾기 퍼즐, 방향키와 Enter로도 플레이할 수 있습니다',
    'Find Nem puzzle. You can also use arrow keys and Enter.',
  ],
  tap: ['넴을 찾으면 터치!', 'Tap 넴 when you find it!'],
  restart: ['다시하기', 'Restart'],
  pause: ['쉬기', 'Pause'],
  resume: ['계속', 'Resume'],
  paused: ['잠깐 쉬는 중', 'Paused'],
  unlimited: ['시간 제한 없음', 'No time limit'],
  seconds: ['{n}초', '{n}s'],
  found: ['{goal}개 중 {score}개 찾음', 'Found {score} of {goal}'],
  resultStarGuide: ['별은 클리어 시간으로 정해져요', 'Stars are based on clear time'],
  perfect: ['오답 없이 클리어!', 'Cleared without mistakes!'],
  clear: ['모두 찾았어요!', 'Puzzle cleared!'],
  fail: ['시간이 다 됐어요', 'Time is up'],
  result: ['{seconds}초 · 오답 {mistakes}회', '{seconds}s · {mistakes} mistakes'],
  combo: ['{n}연속 정답!', '{n} in a row!'],
  hit: ['찾았어요! 다음 넴을 찾아보세요.', 'Found it! Look for the next Nem.'],
  hitEffect: ['정답을 맞히면', 'After a correct answer'],
  transfer: ['점 두 개 이동', 'Move the two tips'],
  shuffle: ['보드 전체 셔플', 'Shuffle the board'],
  slide: ['행·열 밀기', 'Slide rows and columns'],
  rotate: ['90도 회전', 'Rotate 90°'],
  slideHelp: [
    '정답마다 가로·세로를 번갈아 한 칸씩 밀어요. 끝의 글자는 반대편으로 돌아와요.',
    'Each hit alternates a one-slot shift right and down. Edge letters wrap around.',
  ],
  rotateHelp: [
    '글자 배치를 시계 방향으로 돌려요. 빈칸을 포함해 2~8행의 꽉 찬 직사각형으로 만들어 주세요.',
    'Rotate the layout clockwise. Use a complete rectangle of 2–8 rows, including blank cells.',
  ],
  growingPreset: ['커지는 보드 프리셋', 'Growing board preset'],
  growingTitle: ['점점 커지는 넴 찾기', 'Growing Nem hunt'],
  fixedPreset: ['고정 크기로 바꾸기', 'Use a fixed size'],
  startSize: ['시작 크기', 'Starting size'],
  growingHelp: [
    '시작 크기부터 정답마다 한 줄·한 열씩 늘어나요. 최대 8×8, 마지막 정답이면 클리어!',
    'Each correct answer adds a row and column, up to 8×8. The final answer clears the puzzle.',
  ],
  grew: ['{n}×{n} 보드에서 다시 찾아봐요!', 'Find Nem on the {n}×{n} board!'],
  missCount: ['오답 {n}회 · 윗획 두 개를 확인해 보세요', 'Miss {n} · look for the two tips'],
  miss: ['윗획 두 개가 있는 넵이에요', 'That Nep still has its two tips'],
  soundOn: ['소리 켜기', 'Unmute'],
  soundOff: ['소리 끄기', 'Mute'],
  make: ['만들기', 'Create'],
  puzzles: ['퍼즐', 'Puzzles'],
  browsePuzzles: ['퍼즐 더 풀기', 'More puzzles'],
  browseExpanded: ['퍼즐 더 풀기 · 전체 화면', 'More puzzles · opens full screen'],
  stars: ['별 {n}개', '{n} stars'],
  bestTime: ['최고 {seconds}초', 'Best {seconds}s'],
  noRecord: ['미완료', 'Not cleared'],
  starGuide: [
    '제한시간의 ⅓ 이내 ★★★ · ⅔ 이내 ★★ · 클리어 ★',
    '★★★ within ⅓ of the time · ★★ within ⅔ · ★ Clear',
  ],
  remove: ['받침 빼기', 'Remove the support'],
  collapsePreset: ['받침 빼기 와르르', 'Falling supports preset'],
  collapseTitle: ['받침이 사라지는 넴 찾기', 'Falling supports'],
  stages: ['스테이지', 'Stages'],
  stageTitle: ['스테이지 · {n}', 'Stage · {n}'],
  archive: ['보관함', 'Archive'],
  stageNumber: ['스테이지 번호', 'Stage number'],
  jumpStage: ['이동', 'Go'],
  previousPage: ['이전', 'Previous'],
  nextPage: ['다음', 'Next'],
  stageRange: ['{start}–{end} / {total}', '{start}–{end} / {total}'],
  dailyArchive: ['데일리', 'Daily'],
  recentPuzzles: ['최근', 'Recent'],
  communityPuzzles: ['커뮤니티 퍼즐', 'Community puzzles'],
  removePuzzle: ['게시 삭제', 'Remove puzzle'],
  removePuzzleConfirm: [
    '이 퍼즐을 공개 목록에서 삭제할까요?',
    'Remove this puzzle from the public list?',
  ],
  communitySignIn: [
    '계정을 연결한 뒤 추천할 수 있어요.',
    'Connect your account to recommend a puzzle.',
  ],
  communityActionFailed: [
    '변경을 저장하지 못했어요. 다시 눌러 주세요.',
    'Could not save the change. Please try again.',
  ],
  communityLimited: [
    '요청이 많아요. 잠시 기다린 뒤 다시 눌러 주세요.',
    'Too many requests. Please wait a moment and try again.',
  ],
  communityPuzzleMissing: [
    '삭제되었거나 볼 수 없는 퍼즐이에요. 목록을 새로고침해 주세요.',
    'This puzzle was removed or is unavailable. Refresh the list.',
  ],
  savedPuzzles: ['저장한 퍼즐', 'Saved puzzles'],
  savedPuzzlesHint: [
    '저장한 커뮤니티 퍼즐을 다시 풀어보세요.',
    'Community puzzles you saved to play again.',
  ],
  savePuzzle: ['저장', 'Save'],
  puzzleSaved: ['저장됨', 'Saved'],
  recommendPuzzle: ['추천', 'Recommend'],
  libraryIntro: [
    '{n}개 스테이지. 원하는 번호부터 풀어보세요.',
    '{n} stages. Start with any number.',
  ],
  dailyArchiveHint: ['날짜별 퍼즐을 다시 풀어보세요.', 'Revisit a puzzle by date.'],
  recentHint: ['최근 열어본 퍼즐이에요.', 'Puzzles you opened recently.'],
  communityHint: ['다른 플레이어가 만든 퍼즐을 풀어보세요.', 'Play puzzles made by other players.'],
  noPuzzles: ['아직 퍼즐이 없어요.', 'No puzzles here yet.'],
  archiveError: ['목록을 불러오지 못했어요.', 'Could not load these puzzles.'],
  morePuzzles: ['더 보기', 'More'],
  playMenu: ['플레이 메뉴', 'Play menu'],
  openPlayMenu: ['플레이 메뉴로', 'Explore play modes'],
  playMenuDestinations: [
    '다른 퍼즐 · 실시간 대전 · 만들기',
    'More puzzles · Live battles · Puzzle maker',
  ],
  startPuzzle: ['시작', 'Play'],
  puzzleGoal: ['넴 {n}번', '{n} finds'],
  libraryBack: ['게임', 'Game'],
  expand: ['전체 화면으로', 'Open full screen'],
  play: ['퍼즐 플레이하기', 'Play puzzle'],
  expandCaption: ['퍼즐을 더 찾거나 직접 만들어보세요', 'Find more puzzles or create your own'],
  expanding: ['전체 화면을 여는 중…', 'Opening full screen…'],
  expandTimeout: ['열리지 않았다면 다시 눌러 주세요.', 'Not open yet? Tap again.'],
  expandError: ['열지 못했어요. 다시 눌러 주세요.', 'Could not open. Please tap again.'],
  loading: ['퍼즐을 불러오는 중…', 'Loading puzzle…'],
  slowLoading: [
    '연결이 늦어지고 있어요. 잠시 기다리거나 다시 불러와 주세요.',
    'The connection is taking longer. Wait a moment or reload.',
  ],
  reload: ['새로고침', 'Reload'],
  preparing: ['플레이 준비 중…', 'Preparing to play…'],
  loadError: ['퍼즐을 불러오지 못했어요', 'Could not load the puzzle'],
  retryDetail: ['연결을 확인하고 다시 시도해 주세요.', 'Check your connection and try again.'],
  retry: ['다시 불러오기', 'Retry'],
  large: ['전체 화면 퍼즐', 'Full-screen puzzle'],
  previewDetail: [
    '{count}글자 · 넴 {goal}번 찾기 · {time}',
    '{count} letters · {goal} finds · {time}',
  ],
  makerHeading: ['퍼즐 만들기', 'Create a puzzle'],
  makerIntro: [
    '넴을 배치하고 직접 풀어본 뒤 공유하세요.',
    'Place Nem, test your puzzle, then share it.',
  ],
  back: ['게임으로', 'Back to game'],
  draftTitle: ['내가 숨긴 넴', 'My hidden Nem'],
  puzzleName: ['퍼즐 이름', 'Puzzle name'],
  layout: ['배치', 'Layout'],
  grid: ['격자', 'Grid'],
  paragraph: ['문단', 'Paragraph'],
  chat: ['대화', 'Chat'],
  columns: ['한 줄 칸 수', 'Columns'],
  count: ['전체 칸 수', 'Cells'],
  goal: ['찾을 횟수', 'Finds to win'],
  timeLimit: ['제한 시간 (0 = 없음)', 'Time limit (0 = none)'],
  mechanic: ['움직임', 'Movement'],
  still: ['고정', 'Still'],
  collapse: ['와르르', 'Tumble'],
  brush: ['무엇을 놓을까요?', 'What will you place?'],
  editorTools: ['보드 편집 도구', 'Board editing tools'],
  zoomIn: ['확대', 'Zoom in'],
  zoomOut: ['축소', 'Zoom out'],
  zoomReset: ['확대 {n}%. 기본 크기로', 'Zoom {n}%. Reset zoom'],
  panBoard: ['보드 이동', 'Move board'],
  keepEditing: ['계속 편집', 'Keep editing'],
  leaveUnsaved: ['저장 없이 나가기', 'Leave without saving'],
  draftLeave: [
    '저장하지 못한 변경이 있어요. 나갈까요? JSON으로 먼저 보관할 수 있어요.',
    'Some changes are not saved. Leave anyway? You can export JSON first.',
  ],
  draftAutoReady: ['변경하면 자동 저장돼요.', 'Changes save automatically.'],
  draftLoading: ['초안 확인 중…', 'Checking draft…'],
  draftSaving: ['초안 저장 중…', 'Saving draft…'],
  draftAutoSaved: ['초안 자동 저장', 'Draft autosaved'],
  draftRestored: ['자동 저장한 초안을 이어서 편집해요.', 'Continue your autosaved draft.'],
  draftAutoError: [
    '초안을 저장하지 못했어요. 이 화면을 유지하고 다시 시도해 주세요.',
    'Could not save your draft. Keep this page open and try again.',
  ],
  blank: ['빈칸', 'Empty'],
  undo: ['되돌리기', 'Undo'],
  redo: ['다시 적용', 'Redo'],
  save: ['초안 저장', 'Save draft'],
  load: ['불러오기', 'Load draft'],
  paint: ['칸을 눌러 넴을 숨겨보세요.', 'Tap a cell to hide Nem.'],
  test: ['직접 테스트', 'Test puzzle'],
  edit: ['편집으로 돌아가기', 'Back to editing'],
  locked: ['테스트를 클리어하면 공유할 수 있어요', 'Solve your puzzle to share it'],
  publishSharedNotice: [
    '게시하면 다른 플랫폼의 플레이어도 이 퍼즐을 볼 수 있어요. 제목에 개인정보를 넣지 마세요.',
    'Publishing makes this puzzle visible to players across platforms. Keep personal information out of the title.',
  ],
  publishNotice: [
    '버튼을 누르면 현재 Reddit 커뮤니티에 내 계정으로 퍼즐을 게시합니다. 제목과 배치가 다른 사람에게 보입니다.',
    'This posts the puzzle under your account in the current Reddit community. Others can see its title and layout.',
  ],
  editorBoard: ['퍼즐 편집 보드', 'Puzzle editor board'],
  boardScroll: [
    '칸을 눌러 칠하기 · 끌어서 이동 · 두 손가락으로 확대',
    'Tap to paint · drag to move · pinch to zoom',
  ],
  cell: ['{n}번 칸: {cell}', 'Cell {n}: {cell}'],
  makerLegacyGrowth: [
    '이전 성장 규칙이에요. 8열 제한을 적용한 뒤 다시 테스트해 주세요.',
    'This draft uses older growth rules. Apply the 8-column limit and test again.',
  ],
  makerLimitGrowth: ['성장을 8×8까지로 바꾸기', 'Limit growth to 8×8'],
  makerWidthLimit: [
    '새 보드는 최대 8열이에요. 회전 보드는 행도 8개 이하로 맞춰 주세요.',
    'New boards support up to 8 columns. Rotating boards also need 8 rows or fewer.',
  ],
  rangeError: ['칸 수와 숫자 범위를 확인해 주세요.', 'Check the cell count and number ranges.'],
  saved: ['이 기기에 초안을 저장했어요.', 'Draft saved on this device.'],
  saveError: [
    '초안을 저장하지 못했어요. JSON으로 내보내 주세요.',
    'Could not save. Export the JSON instead.',
  ],
  noDraft: ['저장한 초안이 없어요.', 'No saved draft found.'],
  loaded: ['초안을 불러왔어요.', 'Draft loaded.'],
  testError: ['테스트를 시작하지 못했어요.', 'Could not start the test.'],
  publish: ['내 계정으로 Reddit에 게시하기', 'Post to Reddit as me'],
  publishShared: ['커뮤니티에 게시하기', 'Publish to community'],
  publishingPuzzle: ['퍼즐을 게시하는 중…', 'Publishing puzzle…'],
  publishSharedLogin: [
    '계정을 연결한 뒤 다시 게시해 주세요. 초안은 이 기기에 저장돼요.',
    'Connect your account, then publish again. Your draft stays on this device.',
  ],
  proved: ['클리어 완료! 이 퍼즐을 공유할 수 있어요.', 'Puzzle cleared. You can now share it.'],
  publishedLink: ['게시한 퍼즐 열기', 'Open published puzzle'],
  published: ['퍼즐 게시물을 만들었어요!', 'Puzzle posted!'],
  publishError: ['공유하지 못했어요. 다시 시도해 주세요.', 'Could not share. Please try again.'],
  invalidLevel: [
    '레벨 설정을 확인해 주세요. 제목, 숫자 범위, 넵·넴 배치가 필요합니다.',
    'Check the title, number ranges, and the Nep/Nem cells.',
  ],
  LOGIN_REQUIRED: ['Reddit에 로그인한 뒤 다시 시도해 주세요.', 'Sign in to Reddit and try again.'],
  RATE_LIMITED: ['잠시 기다린 뒤 다시 게시해 주세요.', 'Please wait before posting again.'],
  publishCommunityFull: [
    '커뮤니티 퍼즐 보관함이 가득 찼어요. 지금은 새 퍼즐을 공유할 수 없어요.',
    'The community puzzle archive is full. New puzzles cannot be shared right now.',
  ],
  PUBLISH_UNCERTAIN: [
    '게시 여부를 커뮤니티에서 먼저 확인해 주세요.',
    'Check the community for your post before retrying.',
  ],
} satisfies Record<string, readonly [string, string]>;
export type GameMessageKey = keyof typeof messages;
export function t(locale: Locale, key: GameMessageKey, values: Record<string, string | number> = {}): string {
  return format(
    localized(locale, messages[key][0], messages[key][1], (copy) => copy.messages[key]),
    values,
  );
}
/** Explicit `preferred` lists are resolved as given; the default path honours the player's saved choice first. */
export function resolveGameLocale(preferred?: readonly string[]): Locale {
  if (preferred === undefined) {
    const saved = savedLocale();
    if (saved) {
      return saved;
    }
  }
  for (const language of preferred ?? (typeof navigator === 'undefined' ? [] : navigator.languages)) {
    const locale = normalizeGameLocale(language);
    if (locale) {
      return locale;
    }
  }
  return 'en';
}
export function levelTitle(locale: Locale, title: string): string {
  const chat = /^Chat · (\d+)$/.exec(title);
  if (chat?.[1]) {
    return chatPuzzleText(locale, Number(chat[1]))?.title ?? title;
  }
  const stage = /^스테이지 · ([1-9]\d{0,3})$/.exec(title);
  if (stage?.[1]) {
    return t(locale, 'stageTitle', { n: stage[1] });
  }
  const daily = /^오늘의 넴 · (\d{4}-\d{2}-\d{2})$/.exec(title);
  return daily?.[1] ? t(locale, 'daily', { day: daily[1] }) : title;
}
